import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DistrictSimpleDto } from '../../../../common/dtos/district.dto';
import { getIdsFromDefaultItemsForSelect } from '../../helpers/select.utilities';
import { DistrictSearchService } from '../../services/district-search/district-search.service';

@Component({
  selector: 'app-district-multi-select',
  templateUrl: './district-multi-select.component.html',
  styleUrls: ['./district-multi-select.component.scss'],
  standalone: false,
})
export class DistrictMultiSelectComponent implements OnInit {
  districtList: Observable<DistrictSimpleDto[]>;

  districtLibrary: DistrictSimpleDto[] = [];

  searchLoading = false;

  selectAllLoading = false;

  labelId = uuidv4();

  internalSelectedDistrictList: DistrictSimpleDto[] = [];

  currentSearchTerm = '';

  badgeDisplayLimit = 5;

  showAllBadges = false;

  loadingDistricts: boolean;

  @Input() label = 'Search for and select districts';

  @Input() placeholder = 'Search for a district...';

  @Input() clearable = false;

  @Input() defaultDistricts: number[] | DistrictSimpleDto[] | null;

  @Output() readonly selectedDistrictList: EventEmitter<DistrictSimpleDto[]> =
    new EventEmitter();

  @Output() readonly districtsLoaded: EventEmitter<boolean> =
    new EventEmitter();

  constructor(private districtSearch: DistrictSearchService) {}

  ngOnInit(): void {
    this.districtSearch.getAllDistricts().subscribe((districts) => {
      this.districtLibrary = [...districts];
      this.districtList = of(this.districtLibrary);
    });

    this.loadDefaultDistricts();
  }

  loadDefaultDistricts() {
    const districtIds = this.defaultDistricts
      ? getIdsFromDefaultItemsForSelect(this.defaultDistricts)
      : [];

    if (!districtIds.length) {
      this.loadingDistricts = false;
      return;
    }

    this.loadingDistricts = true;
    let districtsRemaining = districtIds.length;
    districtIds.forEach((districtId) => {
      this.districtSearch
        .getDistrict(districtId as number)
        .subscribe((district) => {
          if (district) {
            this.internalSelectedDistrictList = [
              ...this.internalSelectedDistrictList,
              district,
            ].sort((a, b) => a.title.localeCompare(b.title));
          }
          districtsRemaining -= 1;
          if (!districtsRemaining) {
            this.loadingDistricts = false;
            this.districtsLoaded.emit(true);
          }
        });
    });
  }

  onSelectionChange(districts: DistrictSimpleDto[]) {
    this.internalSelectedDistrictList = districts;
    this.outputDistricts();
  }

  onSearch(event: { term: string }) {
    this.currentSearchTerm = event.term;
  }

  selectAllDistricts() {
    this.selectAllLoading = true;
    this.districtSearch.getAllDistricts().subscribe((districts) => {
      this.internalSelectedDistrictList = [...districts].sort((a, b) =>
        a.title.localeCompare(b.title)
      );
      this.selectAllLoading = false;
      this.outputDistricts();
    });
  }

  clearAllDistricts() {
    this.internalSelectedDistrictList = [];
    this.showAllBadges = false;
    this.outputDistricts();
  }

  removeDistrictFromList(district: DistrictSimpleDto) {
    this.internalSelectedDistrictList =
      this.internalSelectedDistrictList.filter(
        (item) => item.id !== district.id
      );
    this.outputDistricts();
  }

  outputDistricts() {
    this.selectedDistrictList.emit(this.internalSelectedDistrictList);
  }

  get visibleBadgeDistricts(): DistrictSimpleDto[] {
    if (this.showAllBadges) {
      return this.internalSelectedDistrictList;
    }
    return this.internalSelectedDistrictList.slice(0, this.badgeDisplayLimit);
  }

  get badgeOverflowCount(): number {
    if (this.showAllBadges) {
      return 0;
    }
    return Math.max(
      0,
      this.internalSelectedDistrictList.length - this.badgeDisplayLimit
    );
  }

  showAllSelectedBadges() {
    this.showAllBadges = true;
  }

  reset() {
    this.internalSelectedDistrictList = [];
    this.showAllBadges = false;
  }
}
